// Wouter route map по Stage 1 §6.1 — 17 routes (15 экранов S-1..S-15 + 2 legal).
// Stage 4 c5 wire-up: S-1..S-15 + legal — реальные экраны.

import { useEffect, useLayoutEffect, useRef } from 'react';
import { Redirect, Route, Switch, useLocation } from 'wouter';
import { router } from '@/lib/router';
import { subscribeToken } from '@/lib/auth-storage';
import { syncPushToken } from '@/lib/push';
import { readListScreenState, screenKeyForPath } from '@/lib/markerNavState';
import { AwaitVerifyProvider } from '@/context/AwaitVerifyContext';
import { PasswordResetProvider } from '@/context/PasswordResetContext';
import { SurveyProvider } from '@/context/SurveyContext';
import { Toaster } from '@/components/ui/toaster';
import { dismissStaleToasts } from '@/hooks/use-toast';
import { OnboardingRouter } from '@/components/guards/OnboardingRouter';
import { RequireAuth } from '@/components/guards/RequireAuth';
import { RequireConsent } from '@/components/guards/RequireConsent';
import { RequireSurveyCompleted } from '@/components/guards/RequireSurveyCompleted';
import { RequireUnauth } from '@/components/guards/RequireUnauth';
import { Landing } from '@/pages/Landing';
import { UploadSheet } from '@/pages/UploadSheet';
import { Processing } from '@/pages/Processing';
import { BatchScreen } from '@/pages/BatchScreen';
import { UploadReviewRedirect } from '@/pages/UploadReviewRedirect';
import { UploadHistory } from '@/pages/UploadHistory';
import { UploadDetail } from '@/pages/UploadDetail';
import { HomePending } from '@/pages/HomePending';
import { UploadDone } from '@/pages/UploadDone';
import { MarkersTab } from '@/pages/MarkersTab';
import { Guide } from '@/pages/Guide';
import { ExtraMarkers } from '@/pages/ExtraMarkers';
import { BiomarkerDetail } from '@/pages/BiomarkerDetail';
import { OrganDetail } from '@/pages/OrganDetail';
import { VisitsTab } from '@/pages/VisitsTab';
import { VisitForm } from '@/pages/VisitForm';
import { VisitDetail } from '@/pages/VisitDetail';
import { ProfileTab } from '@/pages/ProfileTab';
import { DeleteAccount } from '@/pages/DeleteAccount';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppCrashView } from '@/components/AppCrashView';
import { MaintenanceScreen } from '@/components/MaintenanceScreen';
import { useMaintenance } from '@/lib/maintenance';
import { Signup } from '@/pages/Signup';
import { Signin } from '@/pages/Signin';
import { Verified } from '@/pages/Verified';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { ResetPassword } from '@/pages/ResetPassword';
import { VerifyCode } from '@/pages/VerifyCode';
import { VerifyResetCode } from '@/pages/VerifyResetCode';
import { Consent } from '@/pages/Consent';
import { OnboardingIntro } from '@/pages/OnboardingIntro';
import { NotificationsFeed } from '@/pages/NotificationsFeed';
import { LegalPage } from '@/pages/LegalPage';
import {
  SurveyStep1,
  SurveyStep2,
  SurveyStep3,
  SurveyStep4,
  SurveyStep5,
} from '@/pages/SurveySteps';

// Глобальный scroll-to-top на смену route. Использует useLayoutEffect (синхронно ДО paint),
// плюс serial reset через rAF + setTimeout — Chrome iOS делает scroll-restoration после
// pushState, иногда уже после rAF. Скроллим все возможные scrollable-targets, потому что
// в разных браузерах overflow живёт на разных элементах (main / window / documentElement
// / body / scrollingElement).
function ScrollToTop() {
  const [location] = useLocation();
  const prevLocationRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const prev = prevLocationRef.current;
    prevLocationRef.current = location;

    // T-1: Toaster живёт на уровне приложения и переживает навигацию, поэтому
    // сообщение таскалось бы за человеком по всем экранам. Гасим всё, что к
    // этому переходу не относится; плашку моложе секунды не трогаем — её
    // раздал сам код перехода («Анализ удалён» → возврат к списку загрузок,
    // причина отказа распознавания → экран загрузки).
    if (prev !== null && prev !== location) dismissStaleToasts();

    // HEAL-9: возврат из карточки маркера (/markers/:code|extra) на экран-источник —
    // Главную (/) или Маркеры (/markers) — восстанавливаем сохранённый scroll вместо
    // сброса наверх. Источник при remount уже поднял из кэша аккордеоны/фильтр, поэтому
    // высота контента совпадает и scrollTop попадает точно. prev startsWith '/markers/'
    // = пришли именно из детали (а не из соседнего таба). Мульти-фаза (rAF + setTimeout
    // 60) — как у reset, чтобы пережить scroll-restoration мобильных браузеров.
    const restoreKey = prev?.startsWith('/markers/') ? screenKeyForPath(location) : null;
    if (restoreKey) {
      const saved = readListScreenState(restoreKey);
      if (saved) {
        const top = saved.scrollTop;
        const restore = () => {
          document.querySelectorAll('main').forEach((m) => m.scrollTo({ top }));
        };
        restore();
        const rafId = requestAnimationFrame(restore);
        const t0 = setTimeout(restore, 0);
        const t1 = setTimeout(restore, 60);
        return () => {
          cancelAnimationFrame(rafId);
          clearTimeout(t0);
          clearTimeout(t1);
        };
      }
    }

    const reset = () => {
      window.scrollTo({ top: 0 });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.querySelectorAll('main').forEach((m) => m.scrollTo({ top: 0 }));
    };
    reset();
    const rafId = requestAnimationFrame(reset);
    const t0 = setTimeout(reset, 0);
    const t1 = setTimeout(reset, 60);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t0);
      clearTimeout(t1);
    };
  }, [location]);
  return null;
}

function Placeholder({ id, title }: { id: string; title: string }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-2 p-6">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{id}</div>
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      <p className="text-sm text-muted-foreground">
        Заглушка — будет реализован в следующем коммите Stage 4
      </p>
    </div>
  );
}

export default function App() {
  const [, setLocation] = useLocation();

  useEffect(() => {
    router.setPush(setLocation);
  }, [setLocation]);

  // HEAL-46b §9.5 — токен устройства отдаётся серверу при запуске и при каждой
  // смене авторизации. Подписка на токен авторизации, а не вызов после входа в
  // трёх экранах (Signin, VerifyCode, ResetPassword): забыть один из них легко,
  // а следствие — человек без уведомлений и без единого признака, что что-то не
  // так. Внутри — no-op, если нет разрешения, нет авторизации или это web.
  useEffect(() => {
    void syncPushToken();
    return subscribeToken(() => {
      void syncPushToken();
    });
  }, []);

  // HEAL-86 (§8.1): окно техработ — один экран вместо ошибок на каждом.
  const underMaintenance = useMaintenance();
  if (underMaintenance) return <MaintenanceScreen />;

  return (
    <AwaitVerifyProvider>
      <PasswordResetProvider>
      <SurveyProvider>
        <ScrollToTop />
        {/* HEAL-47: граница вокруг экранов, ВНУТРИ провайдеров — после «Попробовать
            снова» роутер и контексты живы, перерисовывается только упавший экран.
            Вторая граница, на случай падения самих провайдеров, стоит в main.tsx. */}
        <ErrorBoundary scope="app-routes" fallback={(reset) => <AppCrashView onRetry={reset} />}>
        <Switch>
          <Route path="/">
            <OnboardingRouter unauthFallback={<Landing />} />
          </Route>
          <Route path="/signup">
            <RequireUnauth>
              <Signup />
            </RequireUnauth>
          </Route>
          <Route path="/signin">
            <RequireUnauth>
              <Signin />
            </RequireUnauth>
          </Route>
          <Route path="/auth/verified" component={Verified} />
          <Route path="/forgot-password">
            <RequireUnauth>
              <ForgotPassword />
            </RequireUnauth>
          </Route>
          {/* OTP password-reset (docs/specs/auth-otp-verification): ввод кода
              между /forgot-password и /auth/reset. Без guard'а (как /verify-code). */}
          <Route path="/verify-reset-code" component={VerifyResetCode} />
          {/* /auth/reset без guard'а: reset-link приходит из email с token+email,
              залогиненный пользователь должен иметь возможность открыть форму.
              После успешного reset backend revoke'ит токены — clearToken() в ResetPassword. */}
          <Route path="/auth/reset" component={ResetPassword} />
          {/* OTP-флоу (docs/specs/auth-otp-verification §4.2 D12). /await-verify
              сохранён как redirect для back-compat (старые deep-link'и / закладки). */}
          <Route path="/verify-code" component={VerifyCode} />
          <Route path="/await-verify">
            <Redirect to="/verify-code" replace />
          </Route>
          <Route path="/consent">
            <RequireAuth>
              <Consent />
            </RequireAuth>
          </Route>
          <Route path="/legal/privacy" component={() => <LegalPage kind="privacy" />} />
          <Route path="/legal/tos" component={() => <LegalPage kind="tos" />} />
          <Route path="/onboarding/intro">
            <RequireAuth>
              <RequireConsent>
                <OnboardingIntro />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/step-1">
            <RequireAuth>
              <RequireConsent>
                <SurveyStep1 />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/step-2">
            <RequireAuth>
              <RequireConsent>
                <SurveyStep2 />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/step-3">
            <RequireAuth>
              <RequireConsent>
                <SurveyStep3 />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/step-4">
            <RequireAuth>
              <RequireConsent>
                <SurveyStep4 />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/step-5">
            <RequireAuth>
              <RequireConsent>
                <SurveyStep5 />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/survey/done">
            <RequireAuth>
              <RequireConsent>
                {/* Sprint 5A.1 (H1 fix): SurveyDone переименован в HomeEmpty и рендерится
                    inline на / через OnboardingRouter. Маршрут /survey/done сохранён для
                    back-compat. wouter 3.9.0 default replace=false → без replace юзер
                    попадает в loop. */}
                <Redirect to="/" replace />
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* HEAL-46a — экран пакета. Свой адрес: переживает перезапуск и
              открывается по ссылке, в том числе из «Моих загрузок». */}
          <Route path="/uploads/batch/:id">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <BatchScreen />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* Мини-спринт C §3.4: гайд показывает интерфейс приложения, поэтому
              стоит под той же цепочкой, что и остальные внутренние экраны, —
              человеку без входа и без анкеты там делать нечего. */}
          <Route path="/guide">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <Guide />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/upload">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <UploadSheet />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/upload/processing/:id">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <Processing />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/upload/review/:id">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <UploadReviewRedirect />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/upload/history">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <UploadHistory />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* HEAL-44a: /uploads — список (алиас на историю) и /uploads/:id — сам анализ. */}
          <Route path="/uploads">
            <Redirect to="/upload/history" />
          </Route>
          {/* HEAL-44a: проверка КОНКРЕТНОГО анализа. Раньше кнопка вела на «/»,
              а там OnboardingRouter показывает первый непроверенный из списка —
              открыв старый анализ из истории, человек правил и подтверждал
              совсем другой файл. */}
          <Route path="/uploads/:id/review">
            {(params) => (
              <RequireAuth>
                <RequireConsent>
                  <RequireSurveyCompleted>
                    <HomePending uploadId={Number(params.id)} />
                  </RequireSurveyCompleted>
                </RequireConsent>
              </RequireAuth>
            )}
          </Route>
          <Route path="/uploads/:id">
            {(params) => (
              <RequireAuth>
                <RequireConsent>
                  <RequireSurveyCompleted>
                    <UploadDetail uploadId={Number(params.id)} />
                  </RequireSurveyCompleted>
                </RequireConsent>
              </RequireAuth>
            )}
          </Route>
          <Route path="/upload/done/:id">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <UploadDone />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* Sprint 5 Stage 4 — Dashboard/Markers routes (R-S5-1).
              wouter first-match: /markers/extra ДОЛЖЕН быть ДО /markers/:code,
              иначе :code='extra' проглотит deep-link на ExtraMarkers. */}
          <Route path="/markers/extra">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <ExtraMarkers />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* Sprint 6-organs — деталь органа. Два сегмента пути, /markers/:code
              (один сегмент) её не проглотит; порядок сохранён для читаемости. */}
          <Route path="/markers/organ/:code">
            {(params) => (
              <RequireAuth>
                <RequireConsent>
                  <RequireSurveyCompleted>
                    <OrganDetail code={params.code} />
                  </RequireSurveyCompleted>
                </RequireConsent>
              </RequireAuth>
            )}
          </Route>
          <Route path="/markers/:code">
            {(params) => (
              <RequireAuth>
                <RequireConsent>
                  <RequireSurveyCompleted>
                    <BiomarkerDetail code={params.code} />
                  </RequireSurveyCompleted>
                </RequireConsent>
              </RequireAuth>
            )}
          </Route>
          <Route path="/markers">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <MarkersTab />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* Mini-sprint 6-visits — wouter first-match: /visits/new и
              /visits/:id/edit ДО /visits/:id, иначе :id='new' проглотит deep-link. */}
          <Route path="/visits/new">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <VisitForm />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/visits/:id/edit">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <VisitForm />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/visits/:id">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <VisitDetail />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/visits">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <VisitsTab />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* HEAL-76 — лента уведомлений. Без RequireSurveyCompleted: история
              того, что человеку писали, не должна зависеть от стадии онбординга. */}
          <Route path="/notifications">
            <RequireAuth>
              <RequireConsent>
                <NotificationsFeed />
              </RequireConsent>
            </RequireAuth>
          </Route>
          <Route path="/profile">
            <RequireAuth>
              <RequireConsent>
                <RequireSurveyCompleted>
                  <ProfileTab />
                </RequireSurveyCompleted>
              </RequireConsent>
            </RequireAuth>
          </Route>
          {/* HEAL-43. Без RequireSurveyCompleted: удалить аккаунт должно быть можно
              на любой стадии, в том числе не закончив анкету — иначе человек, который
              передумал на середине онбординга, останется с аккаунтом навсегда. */}
          <Route path="/profile/delete">
            <RequireAuth>
              <DeleteAccount />
            </RequireAuth>
          </Route>
          <Route>
            <Placeholder id="404" title="Страница не найдена" />
          </Route>
        </Switch>
        </ErrorBoundary>
      </SurveyProvider>
      </PasswordResetProvider>
      <Toaster />
    </AwaitVerifyProvider>
  );
}
